/**
 * SchemaClass represents a class in the domain model (TBox).
 * Implements Node interface for unified handling with Individual (ABox).
 *
 * Renamed from ModelClass as part of the TBox/ABox refactoring.
 */
import Concept from "@semantical/core/modules/vocabulary/Concept";
import { classExpressionToJSON, classExpressionFromJSON } from "./ClassExpression";
/**
 * SchemaClass represents a class in the domain model (TBox).
 * Implements Node interface for unified handling with Individual.
 */
export default class SchemaClass {
    constructor(type, concept, options) {
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "properties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "proposedProperties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "proposedDefinitions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "concept", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        }); // non-null
        // Flattened fields (previously in owlClass)
        /** The class label (may differ from concept.prefLabel) */
        Object.defineProperty(this, "label", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Semantic stereotype (e.g., "Kind", "Role", "Phase") */
        Object.defineProperty(this, "stereotype", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Visual category color from ontology (sem:categoryColor) */
        Object.defineProperty(this, "categoryColor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Data properties (attributes) defined for this class */
        Object.defineProperty(this, "dataProperties", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Private axiom fields (internal use only)
        /** @internal rdfs:subClassOf axioms (includes property restrictions) */
        Object.defineProperty(this, "_subClassOf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** @internal owl:equivalentClass axioms */
        Object.defineProperty(this, "_equivalentClass", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.type = type;
        this.concept = concept;
        if (options) {
            this.label = options.label;
            this.stereotype = options.stereotype;
            this.categoryColor = options.categoryColor;
            this.dataProperties = options.dataProperties;
            this._subClassOf = options.subClassOf;
            this._equivalentClass = options.equivalentClass;
        }
    }
    get name() {
        return this.concept.prefLabel;
    }
    get definition() {
        return this.concept.definition;
    }
    /**
     * The identifying URI for this class.
     * Uses label-derived URI pattern, otherwise falls back to concept.uri.
     */
    get uri() {
        // If we have a label, we can derive a URI pattern
        // For now, concept.uri is the canonical identifier
        return this.concept.uri;
    }
    static fromJSON(json, vocabulary) {
        if (!json.concept || !json.concept.href) {
            throw new Error("SchemaClass.fromJSON: concept reference is required");
        }
        // Try to get concept from vocabulary, or create from stored data if not found
        let concept;
        if (vocabulary.hasConcept(json.concept.href)) {
            concept = vocabulary.getConcept(json.concept.href);
        }
        else {
            // Concept not in vocabulary (e.g., created from toolbox with urn:concept:... URI)
            // Restore from stored data
            concept = new Concept(json.concept.href, json.concept.prefLabel || json.concept.href, [], [], json.concept.definition || "");
        }
        // Restore flattened class data
        const schemaClass = new SchemaClass(json.type, concept, {
            label: json.label,
            stereotype: json.stereotype,
            categoryColor: json.categoryColor,
            dataProperties: json.dataProperties,
            subClassOf: json.subClassOf?.map((expr) => classExpressionFromJSON(expr)),
            equivalentClass: json.equivalentClass?.map((expr) => classExpressionFromJSON(expr))
        });
        schemaClass.properties = json.properties ?? [];
        schemaClass.proposedProperties = json.proposedProperties ?? [];
        schemaClass.proposedDefinitions = json.proposedDefinitions ?? [];
        return schemaClass;
    }
    toJSON() {
        return {
            type: this.type,
            properties: this.properties.map((property) => {
                // convert "+ fuelType: String" to {name: "fuelType", type: "String", cardinality: "1"} using a regexp
                const matches = property.match(/(\+|-)?\s*(\w+)\s*:\s*(\w+)/);
                return {
                    name: matches[2],
                    type: matches[3],
                    cardinality: "1"
                };
            }),
            concept: {
                href: this.concept.uri,
                // Also store full concept data for restoration when concept is not in vocabulary
                prefLabel: this.concept.prefLabel,
                definition: this.concept.definition
            },
            // Flattened fields (no longer nested in owlClass)
            label: this.label,
            stereotype: this.stereotype,
            categoryColor: this.categoryColor,
            dataProperties: this.dataProperties,
            subClassOf: this._subClassOf?.map(expr => classExpressionToJSON(expr)),
            equivalentClass: this._equivalentClass?.map(expr => classExpressionToJSON(expr))
        };
    }
}
/**
 * @deprecated Use SchemaClass instead
 */
export { SchemaClass as ModelClass };
